import { useEffect, useState } from "react";
import { Users, IdCard, AlarmClock, PauseCircle, CalendarX, CalendarDays, ClipboardCheck, Inbox } from "lucide-react";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, AreaChart, Area } from "recharts";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading } from "@/components/common";

const COLORS = ["#1E3A8A", "#10B981", "#D97706", "#0EA5E9", "#EF4444"];

const KPI = [
  ["total_athletes", "Totale atleti", Users, "text-[#0F2042]"],
  ["active_licenses", "Licenze attive", IdCard, "text-emerald-600"],
  ["expiring_licenses", "In scadenza (30 gg)", AlarmClock, "text-amber-600"],
  ["suspended_licenses", "Licenze sospese", PauseCircle, "text-orange-600"],
  ["expired_licenses", "Licenze scadute", CalendarX, "text-slate-500"],
  ["scheduled_events", "Manifestazioni programmate", CalendarDays, "text-blue-700"],
  ["participations", "Partecipazioni registrate", ClipboardCheck, "text-emerald-700"],
  ["pending_requests", "Richieste in attesa", Inbox, "text-amber-700"],
];

const ChartCard = ({ title, children, className = "" }) => (
  <div className={`card-surface p-5 ${className}`}>
    <div className="mb-4 font-display text-base font-semibold text-slate-900">{title}</div>
    <div className="h-64">{children}</div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => { api.get("/dashboard/stats").then((r) => setD(r.data)).catch((e) => setError(errMsg(e))); }, []);
  if (error) return <div className="text-red-700">{error}</div>;
  if (!d) return <Loading />;
  return (
    <div>
      <PageHeader eyebrow={user.role === "admin" ? "Dashboard nazionale" : `Dashboard · ${user.sector_name}`} title="Quadro generale licenze"
        subtitle="Dati in tempo reale dal Registro Nazionale MSP Italia – Settori Motori." />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:gap-4">
        {KPI.map(([k, label, Icon, cls], i) => (
          <div key={k} className="card-surface p-4 fade-up transition-shadow hover:shadow-md sm:p-5" style={{ animationDelay: `${i * 40}ms` }} data-testid={`kpi-${k}`}>
            <div className="flex items-start justify-between">
              <div className="text-xs font-medium text-slate-500">{label}</div>
              <Icon className={`h-4 w-4 ${cls}`} />
            </div>
            <div className={`mt-3 font-display text-3xl font-bold ${cls}`} data-testid={`kpi-${k}-value`}>{d[k]}</div>
          </div>
        ))}
      </div>
      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <ChartCard title="Licenze attive per disciplina">
          <ResponsiveContainer>
            <PieChart>
              <Pie data={d.by_discipline} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                {d.by_discipline.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip /><Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Atleti e licenze per settore" className="lg:col-span-2">
          <ResponsiveContainer>
            <BarChart data={d.by_sector}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip /><Legend />
              <Bar dataKey="atleti" name="Atleti" fill="#1E3A8A" radius={[4, 4, 0, 0]} />
              <Bar dataKey="licenze_attive" name="Licenze attive" fill="#10B981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Andamento mensile (ultimi 12 mesi)" className="lg:col-span-3">
          <ResponsiveContainer>
            <AreaChart data={d.monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="mese" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip /><Legend />
              <Area type="monotone" dataKey="licenze" name="Licenze emesse" stroke="#1E3A8A" fill="#1E3A8A" fillOpacity={0.12} />
              <Area type="monotone" dataKey="partecipazioni" name="Partecipazioni" stroke="#10B981" fill="#10B981" fillOpacity={0.12} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}
